import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash, ArrowLeft } from "@phosphor-icons/react";

export function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"?`)) return;
    await api.delete(`/admin/blog/${slug}`);
    toast.success("Deleted");
    load();
  };

  const generateOne = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/blog/ai-generate", { auto_publish: true });
      toast.success(`AI generated: "${data.title.slice(0, 40)}…"`);
      load();
    } catch (e) {
      toast.error("AI generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Admin · Blog</div>
            <h1 className="font-display text-4xl">All posts</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={generateOne} disabled={busy} className="inline-flex items-center gap-2 bg-accent-red text-white px-5 py-3 font-semibold disabled:opacity-50" data-testid="ai-generate-btn">
              ✨ {busy ? "Generating…" : "AI Generate Now"}
            </button>
            <Link to="/admin/blog/new" className="inline-flex items-center gap-2 bg-brand text-white px-5 py-3 font-semibold" data-testid="new-post-btn">
              <Plus size={18} weight="bold" /> New post
            </Link>
          </div>
        </div>

        <div className="border border-slate-200 bg-emerald-50 p-4 mb-6 text-sm">
          <span className="font-semibold text-emerald-800">🤖 Autopilot is ACTIVE</span> — one AI post auto-generates and publishes every day. Cost: ~$0.03/post via Emergent LLM key.
        </div>

        {posts.length === 0 ? (
          <p className="text-slate-500">No posts yet.</p>
        ) : (
          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Title</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Updated</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.slug} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{p.title}</div>
                      <div className="text-xs text-slate-500 font-mono">/blog/{p.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{new Date(p.updated_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Link to={`/admin/blog/edit/${p.slug}`} className="text-xs uppercase tracking-wider font-semibold text-brand" data-testid={`edit-${p.slug}`}>
                        <Pencil size={12} className="inline" /> Edit
                      </Link>
                      <button onClick={() => remove(p.slug)} className="text-xs uppercase tracking-wider font-semibold text-accent-red" data-testid={`delete-${p.slug}`}>
                        <Trash size={12} className="inline" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export function AdminBlogEditor() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isEdit = !!slug;
  const [form, setForm] = useState({
    title: "", excerpt: "", content: "", cover_image: "", tags: "", status: "draft",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api.get(`/admin/blog`).then((r) => {
        const p = r.data.find((x) => x.slug === slug);
        if (p) setForm({
          title: p.title, excerpt: p.excerpt, content: p.content,
          cover_image: p.cover_image || "", tags: (p.tags || []).join(", "),
          status: p.status,
        });
      });
    }
  }, [slug, isEdit]);

  const save = async (status) => {
    setBusy(true);
    try {
      const body = {
        title: form.title,
        excerpt: form.excerpt,
        content: form.content,
        cover_image: form.cover_image || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        status,
      };
      if (isEdit) {
        await api.put(`/admin/blog/${slug}`, body);
      } else {
        await api.post("/admin/blog", body);
      }
      toast.success(status === "published" ? "Published" : "Saved as draft");
      navigate("/admin/blog");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin/blog" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-6 hover:text-slate-900">
          <ArrowLeft size={14} /> All posts
        </Link>
        <h1 className="font-display text-4xl mb-8">{isEdit ? "Edit post" : "New post"}</h1>
        <div className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5 text-lg" data-testid="post-title" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">Excerpt <span className="lowercase font-normal text-slate-400">(SEO description, ~155 chars)</span></label>
            <textarea required rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5" data-testid="post-excerpt" />
            <div className="text-[10px] text-slate-400 mt-1">{form.excerpt.length} / 155</div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">Cover image URL</label>
            <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://…" className="w-full border border-slate-300 px-3 py-2.5" data-testid="post-cover" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">Tags <span className="lowercase font-normal text-slate-400">(comma separated)</span></label>
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="hiring, plumbing, toronto" className="w-full border border-slate-300 px-3 py-2.5" data-testid="post-tags" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">Content <span className="lowercase font-normal text-slate-400">(Markdown supported)</span></label>
            <textarea required rows={16} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="# Heading&#10;&#10;Write your post in markdown…" className="w-full border border-slate-300 px-3 py-2.5 font-mono text-sm" data-testid="post-content" />
          </div>
          <div className="flex gap-3 pt-2">
            <button disabled={busy} onClick={() => save("draft")} className="bg-white border border-slate-300 text-slate-900 px-5 py-3 font-semibold disabled:opacity-50" data-testid="save-draft-btn">
              Save draft
            </button>
            <button disabled={busy} onClick={() => save("published")} className="bg-brand text-white px-6 py-3 font-semibold disabled:opacity-50" data-testid="publish-btn">
              {form.status === "published" ? "Update published" : "Publish"}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
